import { Modal, ModalFooter } from '@/components/common';
import { CreateDeviceRequest, HoodType, InstitutionDTO, LabDTO } from '@/models';
import React from 'react';

interface DeviceFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: CreateDeviceRequest;
  institutions: InstitutionDTO[];
  labs: LabDTO[];
  isSaving: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onInstitutionChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const DeviceFormModal: React.FC<DeviceFormModalProps> = ({
  isOpen,
  mode,
  formData,
  institutions,
  labs,
  isSaving,
  onInputChange,
  onInstitutionChange,
  onSubmit,
  onClose,
}) => {
  const getInstitutionName = (institutionId: string) => {
    const institution = institutions.find((i) => i.id === institutionId);
    return institution?.name || '-';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title={mode === 'create' ? 'Add New Device' : 'Edit Device'}
      size="lg"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={onSubmit}
          confirmText={mode === 'create' ? 'Create' : 'Update'}
          isLoading={isSaving}
          disabled={mode === 'create' && !formData.institutionId}
        />
      }
    >
      <form>
        <div className="row g-3">
          <div className="col-12">
            <label htmlFor="institutionId" className="form-label">
              Institution <span className="text-danger">*</span>
            </label>
            {mode === 'edit' ? (
              <input
                type="text"
                className="form-control"
                value={getInstitutionName(formData.institutionId)}
                disabled
              />
            ) : (
              <select
                className="form-select"
                id="institutionId"
                name="institutionId"
                value={formData.institutionId}
                onChange={onInstitutionChange}
                required
              >
                <option value="">Select an institution...</option>
                {institutions.map((institution) => (
                  <option key={institution.id} value={institution.id}>
                    {institution.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="col-md-6">
            <label htmlFor="serialNumber" className="form-label">
              Serial Number
            </label>
            <input
              type="text"
              className="form-control"
              id="serialNumber"
              name="serialNumber"
              value={formData.serialNumber || ''}
              onChange={onInputChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="model" className="form-label">
              Model
            </label>
            <input
              type="text"
              className="form-control"
              id="model"
              name="model"
              value={formData.model || ''}
              onChange={onInputChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="identifier" className="form-label">
              Identifier
            </label>
            <input
              type="text"
              className="form-control"
              id="identifier"
              name="identifier"
              value={formData.identifier || ''}
              onChange={onInputChange}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="macAddress" className="form-label">
              MAC Address
            </label>
            <input
              type="text"
              className="form-control"
              id="macAddress"
              name="macAddress"
              value={formData.macAddress || ''}
              onChange={onInputChange}
              placeholder="00:00:00:00:00:00"
            />
          </div>
          <div className="col-12">
            <label htmlFor="labId" className="form-label">
              Lab
            </label>
            <select
              className="form-select"
              id="labId"
              name="labId"
              value={formData.labId ?? ''}
              onChange={onInputChange}
              disabled={!formData.institutionId}
            >
              <option value="">No lab assigned</option>
              {labs
                .filter((lab) => lab.institutionId === formData.institutionId)
                .map((lab) => (
                  <option key={lab.id} value={lab.id}>
                    {lab.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="col-md-6">
            <label htmlFor="hoodType" className="form-label">
              Hood Type
            </label>
            <select
              className="form-select"
              id="hoodType"
              name="hoodType"
              value={formData.hoodType ?? ''}
              onChange={onInputChange}
            >
              <option value="">Not specified</option>
              <option value={HoodType.CAV}>CAV (Constant Air Volume)</option>
              <option value={HoodType.VAV}>VAV (Variable Air Volume)</option>
            </select>
          </div>
        </div>
      </form>
    </Modal>
  );
};
